import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandshake } from "@fortawesome/free-solid-svg-icons";
import "../../../styles/LiveTakeoffHero.css";

/* =========================================================================
   LIVE TAKEOFF HERO  —  Surya Group
   -------------------------------------------------------------------------
   A self-contained, drop-in hero that *proves* we read drawings and produce
   takeoffs. Auto-plays by default (mobile + desktop), supports desktop
   takeover (hover + click to lock), and degrades to a clean static
   "marked-up plan + filled BOQ" when prefers-reduced-motion is set.

   No 3D libs. Pure SVG + CSS transforms. No layout shift.

   TWEAK ME:
   - Brand colors: BRAND below + the CSS :root tokens in LiveTakeoffHero.css
   - Floor plan geometry: WALLS (a/b are start/end points in the 0..460/0..340
     viewBox; tag is where the floating qty label sits)
   - Scopes / line items / costs: SCOPES (all numbers are illustrative demo data)
   ========================================================================= */

// ---- Brand tokens (kept in JS too so SVG strokes can read them) ----------
const BRAND = {
  navy: "#35355D", // primary
  brick: "#8D3233", // secondary
  amber: "#C98A2B", // accent (3rd scope)
};

// ---- Rotating headline slides (left column) — same copy as the old carousel
const SLIDES = [
  {
    eyebrow: "About Surya Group",
    kicker: "WELCOME TO",
    topic: "SURYA GROUP",
    des:
      "At Surya Group, we provide clear and accurate construction estimates with a personal touch. Using the latest tools and technology, we make your project planning and budgeting easy and precise.",
    buttons: [
      { label: "Learn Our Story", to: "/about", kind: "primary" },
      { label: "Get a Free Estimate", to: "/login", kind: "secondary" },
    ],
  },
  {
    eyebrow: "Projects, Delivered",
    kicker: "SPEED MEETS",
    topic: "ACCURACY",
    des: "Efficient, reliable estimates to keep your projects on track — from first blueprint to final bid.",
    buttons: [
      { label: "Send Blueprints", to: "/login", kind: "primary" },
      { label: "Schedule a Meeting", to: "/schedule-meeting", kind: "secondary" },
    ],
  },
  {
    eyebrow: "Built For Every Build",
    kicker: "TAILORED SOLUTIONS FOR",
    topic: "EVERY BUILD",
    des: "Detailed Construction estimations for residential, commercial, and retail projects.",
    buttons: [
      { label: "Read FAQs", to: "/frequently-asked-questions", kind: "primary" },
      { label: "Schedule a Meeting", to: "/schedule-meeting", kind: "secondary" },
    ],
  },
];
const SLIDE_AUTO_MS = 6500;

// Iso "pop-up" offset used for the subtle 2.5D massing (up-left).
const OX = -10;
const OY = -16;

// ---- Floor-plan walls. a=[x,y] start, b=[x,y] end, tag=[x,y] label anchor --
const WALLS = [
  { id: "W1", name: "Wall A", a: [40, 50], b: [420, 50], qty: 128, tag: [300, 38] },
  { id: "W2", name: "Wall B", a: [420, 50], b: [420, 300], qty: 96, tag: [430, 172] },
  { id: "W3", name: "Wall C", a: [420, 300], b: [40, 300], qty: 128, tag: [150, 318] },
  { id: "W4", name: "Wall D", a: [40, 300], b: [40, 50], qty: 96, tag: [16, 172] },
  { id: "W5", name: "Wall E", a: [230, 50], b: [230, 200], qty: 64, tag: [248, 150] },
  { id: "W6", name: "Wall F", a: [230, 200], b: [420, 200], qty: 96, tag: [322, 188] },
];

const dOf = (w) => `M${w.a[0]},${w.a[1]} L${w.b[0]},${w.b[1]}`;
const extOf = (w) =>
  `${w.a[0]},${w.a[1]} ${w.b[0]},${w.b[1]} ${w.b[0] + OX},${w.b[1] + OY} ${w.a[0] + OX},${w.a[1] + OY}`;

// ---- Scopes cycled in the auto-loop. One BOQ row reveals per traced wall. --
const SCOPES = [
  {
    id: "drywall",
    name: "Drywall",
    color: BRAND.navy,
    rows: [
      { item: 'Drywall 1/2" Board', qty: "608 SF", cost: 2128 },
      { item: "Joint Compound", qty: "12 box", cost: 336 },
      { item: "Mesh Tape (500')", qty: "6 roll", cost: 90 },
      { item: "Corner Bead", qty: "14 pc", cost: 112 },
      { item: 'Screws 1-1/4"', qty: "8 box", cost: 152 },
      { item: "Finish — 3-coat (labor)", qty: "608 SF", cost: 1520 },
    ],
  },
  {
    id: "painting",
    name: "Painting",
    color: BRAND.brick,
    rows: [
      { item: "Primer (PVA), 1 coat", qty: "540 SF", cost: 486 },
      { item: "Wall Paint, 2 coat", qty: "540 SF", cost: 972 },
      { item: "Ceiling Paint", qty: "180 SF", cost: 324 },
      { item: "Trim Enamel", qty: "60 LF", cost: 210 },
      { item: "Caulk & Surface Prep", qty: "1 LS", cost: 145 },
      { item: "Masking & Labor", qty: "1 LS", cost: 680 },
    ],
  },
  {
    id: "framing",
    name: "Framing",
    color: BRAND.amber,
    rows: [
      { item: "2x4 Studs", qty: "92 pc", cost: 644 },
      { item: "Bottom Plate (PT)", qty: "64 LF", cost: 192 },
      { item: "Top Plate (double)", qty: "128 LF", cost: 320 },
      { item: "Header 2x10", qty: "3 pc", cost: 135 },
      { item: 'Sheathing 7/16"', qty: "12 sht", cost: 384 },
      { item: "Fasteners & Hardware", qty: "1 LS", cost: 240 },
    ],
  },
];

export default function LiveTakeoffHero() {
  // phase = { scope: index into SCOPES, count: # of walls traced so far }
  const [phase, setPhase] = useState({ scope: 0, count: 0 });
  const [locked, setLocked] = useState([]); // wall indices the user clicked
  const [hover, setHover] = useState(-1);
  const [userActive, setUserActive] = useState(false);
  const [displayTotal, setDisplayTotal] = useState(0);
  const [reduced, setReduced] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  // ---- left-column headline carousel (rotates through SLIDES) ------------
  const [slideActive, setSlideActive] = useState(0);
  const slideTimerRef = useRef(null);
  const totalSlides = SLIDES.length;

  const restartSlideAutoplay = useCallback(() => {
    clearTimeout(slideTimerRef.current);
    slideTimerRef.current = setTimeout(() => {
      setSlideActive((a) => (a + 1) % totalSlides);
    }, SLIDE_AUTO_MS);
  }, [totalSlides]);

  useEffect(() => {
    restartSlideAutoplay();
    return () => clearTimeout(slideTimerRef.current);
  }, [slideActive, restartSlideAutoplay]);

  const goToSlide = (i) => setSlideActive(((i % totalSlides) + totalSlides) % totalSlides);

  // ---- "AI writing" typewriter effect on the active slide's paragraph ----
  const [typedLen, setTypedLen] = useState(0);
  const typeTimerRef = useRef(null);

  useEffect(() => {
    const text = SLIDES[slideActive].des;
    if (reduced) {
      setTypedLen(text.length);
      return;
    }
    setTypedLen(0);
    let i = 0;
    clearInterval(typeTimerRef.current);
    typeTimerRef.current = setInterval(() => {
      i += 1;
      setTypedLen(i);
      if (i >= text.length) clearInterval(typeTimerRef.current);
    }, 16);
    return () => clearInterval(typeTimerRef.current);
  }, [slideActive, reduced]);

  const scopeRef = useRef(0);
  const countRef = useRef(0);
  const tickRef = useRef(null);
  const resumeRef = useRef(null);
  const pausedRef = useRef(false);
  const displayRef = useRef(0);
  const ctaRef = useRef(null);

  // ---- environment detection (reduced motion + touch) --------------------
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const tp = window.matchMedia("(hover: none), (pointer: coarse)");
    setReduced(rm.matches);
    setIsTouch(tp.matches);
    const onRm = (e) => setReduced(e.matches);
    rm.addEventListener?.("change", onRm);
    return () => rm.removeEventListener?.("change", onRm);
  }, []);

  // ---- the auto-play takeoff sequencer -----------------------------------
  useEffect(() => {
    if (reduced) {
      // Static "after" state: fully marked-up plan + filled BOQ (no timers).
      setPhase({ scope: 0, count: WALLS.length });
      return;
    }
    let cancelled = false;
    const schedule = (ms) => {
      tickRef.current = setTimeout(tick, ms);
    };
    const tick = () => {
      if (cancelled) return;
      if (pausedRef.current) {
        schedule(400); // user is driving — idle until they stop
        return;
      }
      let c = countRef.current;
      let s = scopeRef.current;
      c += 1;
      let delay = 950; // pace between walls
      if (c > WALLS.length) {
        // Whole scope done → clear and advance to the next scope.
        s = (s + 1) % SCOPES.length;
        c = 0;
        delay = 900; // brief empty beat so the new scope reads as fresh
      } else if (c === WALLS.length) {
        delay = 2000; // hold the completed scope so it's legible
      }
      scopeRef.current = s;
      countRef.current = c;
      setPhase({ scope: s, count: c });
      schedule(delay);
    };
    schedule(700);
    return () => {
      cancelled = true;
      clearTimeout(tickRef.current);
    };
  }, [reduced]);

  // ---- smoothly tick the running cost total toward its target ------------
  const scope = SCOPES[phase.scope];
  const revealedRows = scope.rows.slice(0, phase.count);
  const lockCost = (idx) => Math.round(WALLS[idx].qty * 3.5); // demo lock value
  const target =
    revealedRows.reduce((s, r) => s + r.cost, 0) +
    locked.reduce((s, i) => s + lockCost(i), 0);

  useEffect(() => {
    let raf;
    const startVal = displayRef.current;
    const startT = performance.now();
    const dur = 600;
    const loop = (now) => {
      const k = Math.min(1, (now - startT) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      const v = startVal + (target - startVal) * eased;
      displayRef.current = v;
      setDisplayTotal(v);
      if (k < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  // ---- desktop takeover: pause/resume the loop on interaction ------------
  const pauseLoop = useCallback(() => {
    if (reduced) return;
    pausedRef.current = true;
    setUserActive(true);
    clearTimeout(resumeRef.current);
  }, [reduced]);

  const scheduleResume = useCallback(
    (ms = 3500) => {
      if (reduced) return;
      clearTimeout(resumeRef.current);
      resumeRef.current = setTimeout(() => {
        pausedRef.current = false;
        setUserActive(false);
        setLocked([]); // drop user picks, hand control back to the loop
        setHover(-1);
      }, ms);
    },
    [reduced]
  );

  const lockWall = useCallback(
    (idx) => {
      if (reduced) return;
      pauseLoop();
      setLocked((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
      scheduleResume(4500);
    },
    [reduced, pauseLoop, scheduleResume]
  );

  useEffect(() => () => clearTimeout(resumeRef.current), []);

  // ---- magnetic pull on the primary CTA toward the cursor ----------------
  const onHeroMove = useCallback(
    (e) => {
      if (reduced || isTouch || !ctaRef.current) return;
      const r = ctaRef.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const R = 170;
      if (dist < R) {
        const f = (1 - dist / R) * 0.35;
        ctaRef.current.style.transform = `translate(${dx * f}px, ${dy * f}px)`;
      } else {
        ctaRef.current.style.transform = "";
      }
    },
    [reduced, isTouch]
  );
  const resetCta = useCallback(() => {
    if (ctaRef.current) ctaRef.current.style.transform = "";
  }, []);

  // ---- which walls are currently "taken off" (drawn) ---------------------
  const isDrawn = (i) =>
    reduced || i < phase.count || locked.includes(i);
  const activeWall =
    !reduced && phase.count > 0 ? WALLS[phase.count - 1] : null;

  const money = (n) =>
    "$" + Math.round(n).toLocaleString("en-US");

  return (
    <section
      className="lt-hero"
      onMouseMove={onHeroMove}
      onMouseLeave={resetCta}
      aria-label="Live construction takeoff demonstration"
    >
      <div className="lt-grid">
        {/* ----------------------------- COPY ----------------------------- */}
        <div className="lt-copy">
          <div
            className="lt-carousel"
            onMouseEnter={() => clearTimeout(slideTimerRef.current)}
            onMouseLeave={restartSlideAutoplay}
          >
            {SLIDES.map((slide, i) => (
              <div
                key={slide.topic}
                className={"lt-slide" + (i === slideActive ? " is-active" : "")}
                aria-hidden={i !== slideActive}
              >
                <span className="lt-eyebrow">
                  <span className="lt-dot" /> {slide.eyebrow}
                </span>
                <p className="lt-slide-kicker">{slide.kicker}</p>
                {i === 0 ? <h1 className="lt-headline">{slide.topic}</h1> : <p className="lt-headline">{slide.topic}</p>}
                <p className="lt-sub">
                  <span className="visually-hidden">{slide.des}</span>
                  <span aria-hidden="true">{i === slideActive ? slide.des.slice(0, typedLen) : slide.des}</span>
                  {i === slideActive && typedLen < slide.des.length && (
                    <span className="lt-type-cursor" aria-hidden="true" />
                  )}
                </p>

                <div className="lt-cta-wrap">
                  {slide.buttons.map((btn) =>
                    btn.kind === "primary" ? (
                      <Link
                        key={btn.label}
                        to={btn.to}
                        className="lt-cta"
                        ref={i === slideActive ? ctaRef : null}
                      >
                        {btn.label}
                        <svg viewBox="0 0 24 24" aria-hidden="true" className="lt-cta-arrow">
                          <path
                            d="M5 12h14M13 6l6 6-6 6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Link>
                    ) : btn.to ? (
                      <Link
                        key={btn.label}
                        to={btn.to}
                        className={
                          "lt-cta-secondary" +
                          (btn.to === "/schedule-meeting" ? " lt-cta-pulse" : "")
                        }
                      >
                        {btn.label}
                      </Link>
                    ) : (
                      <a
                        key={btn.label}
                        href={btn.href}
                        className={
                          "lt-cta-secondary" +
                          (btn.href === "/schedule-meeting" ? " lt-cta-pulse" : "")
                        }
                      >
                        {btn.label}
                      </a>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="lt-slide-nav">
            <div className="lt-slide-dots" role="tablist" aria-label="Select highlight">
              {SLIDES.map((slide, i) => (
                <button
                  key={slide.topic}
                  role="tab"
                  aria-selected={i === slideActive}
                  aria-label={`Show highlight ${i + 1}: ${slide.topic}`}
                  className={"lt-slide-dot" + (i === slideActive ? " is-active" : "")}
                  onClick={() => goToSlide(i)}
                >
                  {i === slideActive && (
                    <span
                      key={slideActive}
                      className="lt-slide-dot-progress"
                      style={{ animationDuration: `${SLIDE_AUTO_MS}ms` }}
                    />
                  )}
                </button>
              ))}
            </div>
            <span className="lt-slide-count">
              <strong>{String(slideActive + 1).padStart(2, "0")}</strong>
              &nbsp;/&nbsp;{String(totalSlides).padStart(2, "0")}
            </span>
          </div>

          <div className="lt-trust">
            <span className="lt-trust-icon-wrap">
              <FontAwesomeIcon icon={faHandshake} aria-hidden="true" />
            </span>
            <p>
              Trusted across North America by general contractors, sub-contractors,
              architects &amp; engineers.
            </p>
          </div>
        </div>

        {/* ----------------------------- DEMO ----------------------------- */}
        <div
          className={`lt-demo${userActive ? " is-active" : ""}`}
          onMouseEnter={pauseLoop}
          onMouseLeave={() => scheduleResume()}
        >
          {/* --- Drawing sheet --- */}
          <div className="lt-sheet">
            <div className="lt-sheet-head">
              <span className="lt-sheet-id">SHEET A-101 · FLOOR PLAN</span>
              <span
                className="lt-scope-badge"
                style={{ background: scope.color }}
              >
                {reduced ? "Marked up" : scope.name}
              </span>
            </div>

            <svg
              viewBox="0 0 460 340"
              className="lt-svg"
              role="img"
              aria-label={`Floor plan with ${scope.name} takeoff traced over the walls`}
            >
              {/* faint construction grid */}
              <defs>
                <pattern
                  id="ltGrid"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M20 0H0V20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    className="lt-grid-line"
                  />
                </pattern>
              </defs>
              <rect x="0" y="0" width="460" height="340" fill="url(#ltGrid)" />

              {/* 2.5D massing — extruded faces for taken-off walls */}
              <g className="lt-mass">
                {WALLS.map((w, i) =>
                  isDrawn(i) ? (
                    <polygon
                      key={w.id}
                      points={extOf(w)}
                      fill={scope.color}
                      className="lt-ext"
                    />
                  ) : null
                )}
              </g>

              {/* base (faint, always present) plan walls */}
              <g className="lt-base">
                {WALLS.map((w) => (
                  <path key={w.id} d={dOf(w)} className="lt-base-wall" />
                ))}
              </g>

              {/* door (in Wall C) + swing arc */}
              <path d="M150,300 A40,40 0 0,1 190,260" className="lt-door-arc" />
              <line x1="150" y1="300" x2="150" y2="260" className="lt-door-leaf" />

              {/* window (in Wall A) */}
              <g className="lt-window">
                <line x1="250" y1="50" x2="330" y2="50" />
                <line x1="250" y1="47" x2="330" y2="47" />
              </g>

              {/* room labels */}
              <text x="120" y="180" className="lt-room">
                ROOM 1
              </text>
              <text x="312" y="135" className="lt-room">
                ROOM 2
              </text>
              <text x="312" y="258" className="lt-room">
                ROOM 3
              </text>

              {/* colored takeoff strokes (animated draw-on) */}
              <g className="lt-trace">
                {WALLS.map((w, i) => (
                  <path
                    key={w.id}
                    d={dOf(w)}
                    pathLength="100"
                    stroke={scope.color}
                    className={
                      "lt-wall" +
                      (isDrawn(i) ? " is-drawn" : "") +
                      (hover === i ? " is-hover" : "") +
                      (locked.includes(i) ? " is-locked" : "")
                    }
                    onMouseEnter={() => !isTouch && setHover(i)}
                    onMouseLeave={() => !isTouch && setHover(-1)}
                    onClick={() => lockWall(i)}
                  >
                    <title>{`${w.name} — ${w.qty} SF`}</title>
                  </path>
                ))}
              </g>

              {/* floating qty tag at the active trace point */}
              {activeWall && (
                <g
                  className="lt-qtytag"
                  key={`${phase.scope}-${phase.count}`}
                  transform={`translate(${activeWall.tag[0]} ${activeWall.tag[1]})`}
                >
                  <rect x="-52" y="-13" width="104" height="22" rx="5" fill={scope.color} />
                  <text x="0" y="2" className="lt-qtytag-txt">
                    {`${activeWall.name} · ${activeWall.qty} SF`}
                  </text>
                </g>
              )}

              {/* scale tag + north arrow (real-sheet annotations) */}
              <text x="40" y="328" className="lt-scale">
                SCALE: 1/4" = 1'-0"
              </text>
              <g className="lt-north" transform="translate(424,316)">
                <path d="M0,-12 L5,8 L0,3 L-5,8 Z" />
                <text x="0" y="20" textAnchor="middle">
                  N
                </text>
              </g>
            </svg>

            <p className="lt-hint" aria-hidden="true">
              {reduced
                ? "Before → after: plan marked up, quantities tallied."
                : isTouch
                ? "Auto-tracing your takeoff…"
                : "Hover a wall to highlight · click to lock it into the estimate"}
            </p>
          </div>

          {/* --- Live BOQ panel --- */}
          <aside className="lt-boq" aria-label="Live bill of quantities">
            <div className="lt-boq-head">
              <span className="lt-boq-title">BILL OF QUANTITIES</span>
              <span className="lt-boq-scope">
                <i style={{ background: scope.color }} />
                {scope.name}
              </span>
            </div>

            <div className="lt-boq-cols">
              <span>Item</span>
              <span>Qty</span>
              <span>Cost</span>
            </div>

            <ul className="lt-boq-list">
              {revealedRows.map((r, i) => (
                <li key={`${scope.id}-${i}`} className="lt-boq-row">
                  <span className="lt-bi">{r.item}</span>
                  <span className="lt-bq">{r.qty}</span>
                  <span className="lt-bc">{money(r.cost)}</span>
                </li>
              ))}
              {locked.map((idx) => (
                <li key={`lock-${idx}`} className="lt-boq-row is-locked">
                  <span className="lt-bi">
                    {WALLS[idx].name} <em>· locked</em>
                  </span>
                  <span className="lt-bq">{WALLS[idx].qty} SF</span>
                  <span className="lt-bc">{money(lockCost(idx))}</span>
                </li>
              ))}
              {revealedRows.length === 0 && locked.length === 0 && (
                <li className="lt-boq-empty">Reading drawing…</li>
              )}
            </ul>

            <div className="lt-boq-total">
              <span>Estimated total</span>
              <span className="lt-total-num" aria-live="polite">
                {money(displayTotal)}
              </span>
            </div>
            <p className="lt-boq-note">Illustrative demo figures</p>
          </aside>
        </div>
      </div>
    </section>
  );
}
